import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, Building2, Phone, FileText, Sparkles, Inbox } from 'lucide-react';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Onboarding checklist shown on the dashboard.
 *
 * Mobile-first: stacked big cards with a single tappable target each.
 * The component fetches /api/onboarding/state and re-fetches on demand
 * via the `refreshKey` prop (changing the prop triggers a refetch -- useful
 * after the user runs the "Test my AI receptionist" button).
 */
export function OnboardingChecklist({ refreshKey = 0, onCompleted }) {
  const { language } = useLanguage();
  const { workspace } = useAuth();
  const tf = (fr, en) => (language === 'fr' ? fr : en);
  const [state, setState] = useState(null);
  const [hidden, setHidden] = useState(false);

  // Plan-aware feature flags — plan is the SOURCE OF TRUTH for feature access.
  // The derived `workspace.sms_enabled` field can fall out of sync if a
  // workspace was upgraded before the plan-sync migration ran, so we read the
  // plan directly: Starter -> telephony/AI locked, Pro/Enterprise -> unlocked.
  const planId = String(workspace?.plan || 'starter').toLowerCase();
  const smsEnabled = planId === 'pro' || planId === 'enterprise';

  useEffect(() => {
    let active = true;
    api.get('/onboarding/state')
      .then(({ data }) => {
        if (!active) return;
        setState(data);
        if (data.progress.percent === 100 && onCompleted) onCompleted();
      })
      .catch(() => { /* silent -- checklist is optional UX */ });
    return () => { active = false; };
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state || hidden) return null;
  const { checklist } = state;

  // Full set of steps. `requires` is the workspace feature flag that must be
  // truthy for the step to render. Steps without `requires` always render.
  const ALL_STEPS = [
    {
      key: 'company_filled',
      icon: Building2,
      title: tf('Ajouter votre entreprise', 'Add your company'),
      hint: tf('Nom, adresse, logo', 'Name, address, logo'),
      link: '/settings',
    },
    {
      key: 'domain_filled',
      icon: Building2,
      title: tf('Definir votre domaine principal', 'Set your primary domain'),
      hint: tf('Verrouille comme identite officielle de votre espace', 'Locked as your workspace identity'),
      link: '/settings',
    },
    {
      key: 'phone_filled',
      icon: Phone,
      title: tf('Renseigner votre telephone', 'Add your phone number'),
      hint: tf('Pour les SMS et confirmations', 'For SMS and confirmations'),
      link: '/settings',
      requires: 'sms_enabled',
    },
    {
      key: 'form_created',
      icon: FileText,
      title: tf('Creer votre premier formulaire', 'Create your first form'),
      hint: tf('Pour collecter vos prospects', 'To collect your leads'),
      link: '/forms/new',
    },
    {
      key: 'receptionist_tested',
      icon: Sparkles,
      title: tf('Tester votre receptionniste IA', 'Test your AI receptionist'),
      hint: tf('Cliquez sur le bouton ci-dessous', 'Use the button below'),
      link: null,
      requires: 'sms_enabled',
    },
    {
      key: 'first_lead',
      icon: Inbox,
      title: tf('Voir votre premier prospect', 'See your first lead'),
      hint: tf('Quand un client soumettra votre formulaire', 'When a real lead arrives'),
      link: '/leads',
    },
  ];

  // Plan-aware filter. Starter (sms_enabled=false) hides telephony + AI
  // onboarding steps; Pro/Enterprise see the full checklist.
  const FEATURE_FLAGS = {
    sms_enabled: smsEnabled,
  };
  const STEPS = ALL_STEPS.filter(
    (s) => !s.requires || FEATURE_FLAGS[s.requires] === true,
  );

  // Recompute progress LOCALLY based on visible steps only — the backend's
  // /onboarding/state counts all 5 keys; on Starter that would lock the
  // % below 100 forever because the hidden telephony/AI steps stay unticked.
  const visibleTotal = STEPS.length;
  const visibleDone = STEPS.filter((s) => !!checklist[s.key]).length;
  const visiblePercent = visibleTotal
    ? Math.round((visibleDone / visibleTotal) * 100)
    : 0;
  if (visiblePercent === 100) {
    if (onCompleted) onCompleted();
    return null;
  }
  const progress = { done: visibleDone, total: visibleTotal, percent: visiblePercent };

  return (
    <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50/60 to-orange-50/40" data-testid="onboarding-checklist">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-lg md:text-xl font-black tracking-tight flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              {tf('On configure votre bureau virtuel', "Let's set up your virtual office")}
            </CardTitle>
            <p className="text-xs md:text-sm text-muted-foreground">
              {tf(
                `${progress.done} sur ${progress.total} etapes completees`,
                `${progress.done} of ${progress.total} steps complete`
              )}
            </p>
          </div>
          <button
            onClick={() => setHidden(true)}
            className="text-xs text-muted-foreground hover:text-foreground"
            data-testid="checklist-hide-btn"
            type="button"
          >
            {tf('Masquer', 'Hide')}
          </button>
        </div>
        <div className="pt-2">
          <Progress value={progress.percent} className="h-2" data-testid="checklist-progress" />
          <p className="mt-1 text-right text-xs font-bold text-amber-600" data-testid="checklist-percent">
            {progress.percent}%
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {STEPS.map((step) => {
          const done = !!checklist[step.key];
          const Icon = step.icon;
          const inner = (
            <div
              className={`flex items-center gap-3 md:gap-4 rounded-xl p-3 md:p-4 transition-all
                ${done
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-white border border-border hover:border-amber-300 hover:shadow-sm cursor-pointer'}`}
              data-testid={`checklist-step-${step.key}`}
            >
              <div className={`flex h-10 w-10 md:h-12 md:w-12 shrink-0 items-center justify-center rounded-full
                ${done ? 'bg-green-500 text-white' : 'bg-amber-100 text-amber-700'}`}>
                {done ? <Check className="h-5 w-5 md:h-6 md:w-6" /> : <Icon className="h-5 w-5 md:h-6 md:w-6" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-bold text-sm md:text-base ${done ? 'line-through text-green-700' : ''}`}>
                  {step.title}
                </p>
                <p className="text-xs md:text-sm text-muted-foreground truncate">{step.hint}</p>
              </div>
              {done ? (
                <Badge className="bg-green-500 text-white border-0 hidden sm:flex">
                  {tf('Fait', 'Done')}
                </Badge>
              ) : step.link ? (
                <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
              ) : null}
            </div>
          );
          return step.link && !done ? (
            <Link key={step.key} to={step.link}>{inner}</Link>
          ) : (
            <div key={step.key}>{inner}</div>
          );
        })}
      </CardContent>
    </Card>
  );
}
